import React, { useEffect, useMemo, useState } from "react";
import {
  CompetitionGroup,
  CompetitionStage,
  GroupStanding,
  ID,
  Match,
  TournamentParticipant,
} from "../../../../types";
import { Button } from "../../Button";
import { MatchesTable } from "./MatchesTable";
import { GroupParticipantsModal } from "./GroupParticipantsModal";
import { useTournamentEvent } from "../TournamentEventContext";

interface CompetitionStageGroupsProps {
  stage: CompetitionStage;
  generatedKnockoutStage: CompetitionStage | null;
  stageGroups: CompetitionGroup[];
  stageMatches: Match[];
  standingsByGroup: Map<ID, GroupStanding[]>;
  participants: TournamentParticipant[];
  participantsById: Map<ID, TournamentParticipant>;
  assignableParticipants: TournamentParticipant[];
  generatingKnockout: boolean;
  updatingGroupId: ID | null;
  onGenerateKnockout: () => Promise<void>;
  onUpdateGroupParticipants: (groupId: ID, participantIds: ID[]) => Promise<void>;
}

export const CompetitionStageGroups: React.FC<CompetitionStageGroupsProps> = ({
  stage,
  generatedKnockoutStage,
  stageGroups,
  stageMatches,
  standingsByGroup,
  participants,
  participantsById,
  assignableParticipants,
  generatingKnockout,
  updatingGroupId,
  onGenerateKnockout,
  onUpdateGroupParticipants,
}) => {
  const { canManage } = useTournamentEvent();
  const stageMatchesMap = useMemo(() => new Map(stageMatches.map(m => [m.id, m])), [stageMatches]);
  const [editingGroupId, setEditingGroupId] = useState<ID | null>(null);
  const [draftParticipantIds, setDraftParticipantIds] = useState<Record<number, ID[]>>({});
  const canGenerateKnockout =
    canManage &&
    stage.stage_type === "groups" &&
    (stage.ranking_start_position === null || stage.ranking_start_position < 1) &&
    stage.status === "played" &&
    !generatedKnockoutStage;
  const canEditGroupParticipants =
    canManage &&
    stage.stage_type === "groups" &&
    stage.status !== "played";

  useEffect(() => {
    const nextDrafts: Record<number, ID[]> = {};
    stageGroups.forEach((group) => {
      nextDrafts[Number(group.id)] = [...group.participants];
    });
    setDraftParticipantIds(nextDrafts);
    setEditingGroupId(null);
  }, [stageGroups]);

  const editingGroup = useMemo(
    () => stageGroups.find((group) => group.id === editingGroupId) ?? null,
    [stageGroups, editingGroupId]
  );

  const selectableParticipantIds = useMemo(() => {
    if (!editingGroup) return new Set<ID>();
    const currentGroupSet = new Set(editingGroup.participants);
    const occupiedElsewhere = new Set<ID>();
    stageGroups.forEach((group) => {
      if (group.id === editingGroup.id) return;
      group.participants.forEach((participantId) => occupiedElsewhere.add(participantId));
    });
    return new Set(
      participants
        .filter(
          (participant) =>
            currentGroupSet.has(participant.id) || !occupiedElsewhere.has(participant.id)
        )
        .map((participant) => participant.id)
    );
  }, [editingGroup, participants, stageGroups]);

  const editingSelectedParticipantIds = editingGroup
    ? (draftParticipantIds[Number(editingGroup.id)] ?? editingGroup.participants)
    : [];
  
  if (stageGroups.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      {(canGenerateKnockout || generatedKnockoutStage) && (
        <div className="bg-white border border-gray-300 rounded shadow-sm p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="font-semibold">Fase successiva</h3>
              <p className="text-sm text-gray-600">
                {generatedKnockoutStage
                  ? `Fase successiva gia generata: ${generatedKnockoutStage.name}`
                  : "Genera automaticamente la fase eliminatoria dai risultati dei gironi."}
              </p>
            </div>
            {canGenerateKnockout && (
              <Button onClick={onGenerateKnockout} disabled={generatingKnockout}>
                {generatingKnockout ? "Generazione..." : "Genera fase eliminatoria"}
              </Button>
            )}
          </div>
        </div>
      )}

      {stageGroups.map((group) => {
        const groupStandings = (standingsByGroup.get(group.id) || []).sort(
          (a, b) => (a.position ?? 9999) - (b.position ?? 9999)
        );
        const groupMatches = stageMatches.filter((match) => match.group === group.id);
        const isSavingGroup = updatingGroupId === group.id;

        return (
          <div key={group.id} className="bg-white border border-gray-300 rounded shadow-sm p-5 mt-6">
            <div className="mb-4 flex flex-col">
              <div className="flex justify-between">
                <h3 className="font-semibold">{group.name}</h3>
                {canEditGroupParticipants && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="warning"
                      onClick={() => setEditingGroupId(group.id)}
                      disabled={isSavingGroup}
                    >
                      {isSavingGroup ? "Salvataggio..." : "Modifica"}
                    </Button>
                  </div>
                )}
              </div>
                <p className="text-sm text-gray-600">
                  Partecipanti nel girone: {group.participants.length}
                </p>
            </div>

            <div className="overflow-x-auto mb-4">
              <table className="min-w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-200 text-gray-700 border-b text-left">
                    <th className="p-2 text-left">#</th>
                    <th className="p-2 text-left">Partecipante</th>
                    <th className="p-2 text-left" title="Giocati">G</th>
                    <th className="p-2 text-left" title="Vinti">V</th>
                    <th className="p-2 text-left" title="Nullo">N</th>
                    <th className="p-2 text-left" title="Persi">P</th>
                    <th className="p-2 text-left hidden sm:table-cell" title="Punti Fatti">Pt F</th>
                    <th className="p-2 text-left hidden sm:table-cell" title="Punti Subiti">Pt S</th>
                    <th className="p-2 text-left" title="Punti Totali">Pt</th>
                  </tr>
                </thead>
                <tbody>
                  {groupStandings.map((standing) => {
                    const isPodium = standing.position && stage.qualifiers_per_group && standing.position <= stage.qualifiers_per_group;
                    return (
                    <tr key={standing.id} className={`border-b ${isPodium ? "bg-yellow-100" : "bg-white"}`}>
                      <td className="p-2">{standing.position ?? "—"}</td>
                      <td className="p-2 font-medium">
                        {participantsById.get(standing.participant)?.name ?? `#${standing.participant}`}
                      </td>
                      <td className="p-2">{standing.played}</td>
                      <td className="p-2">{standing.wins}</td>
                      <td className="p-2">{standing.draws}</td>
                      <td className="p-2">{standing.losses}</td>
                      <td className="p-2 hidden sm:table-cell">{standing.points_scored}</td>
                      <td className="p-2 hidden sm:table-cell">{standing.points_conceded}</td>
                      <td className="p-2">{standing.points}</td>
                    </tr>
                  )
                  })}
                </tbody>
              </table>
            </div>

            <MatchesTable
              title="Match del girone"
              stageMatches={stageMatchesMap}
              matches={groupMatches}
              participantsById={participantsById}
              participants={assignableParticipants}
              getSlotLabel={() => "TBD"}
            />
          </div>
        );
      })}

      <GroupParticipantsModal
        open={!!editingGroup}
        group={editingGroup}
        participants={participants}
        selectedParticipantIds={editingSelectedParticipantIds}
        selectableParticipantIds={selectableParticipantIds}
        saving={editingGroup ? updatingGroupId === editingGroup.id : false}
        onClose={() => setEditingGroupId(null)}
        onToggleParticipant={(participantId) => {
          if (!editingGroup || !selectableParticipantIds.has(participantId)) return;
          setDraftParticipantIds((prev) => {
            const current = prev[Number(editingGroup.id)] ?? editingGroup.participants;
            const next = current.includes(participantId)
              ? current.filter((id) => id !== participantId)
              : [...current, participantId];
            return { ...prev, [Number(editingGroup.id)]: next };
          });
        }}
        onReset={() => {
          if (!editingGroup) return;
          setDraftParticipantIds((prev) => ({
            ...prev,
            [Number(editingGroup.id)]: [...editingGroup.participants],
          }));
        }}
        onConfirm={async () => {
          if (!editingGroup || updatingGroupId === editingGroup.id) return;
          if (editingSelectedParticipantIds.length < 2) {
            alert("Ogni girone deve avere almeno 2 partecipanti.");
            return;
          }
          const confirmed = window.confirm(
            "Salvando i partecipanti del girone i match associati verranno ricreati. Vuoi continuare?"
          );
          if (!confirmed) return;
          await onUpdateGroupParticipants(editingGroup.id, editingSelectedParticipantIds);
        }}
      />
    </div>
  );
};
